/** Shared audio types — JSON-serializable for TS kernel and future Rust/Wasm. */

export type Waveform = "sine" | "tri" | "saw" | "square" | "noise";

export type Adsr = {
  a: number;
  d: number;
  s: number;
  r: number;
};

export type OscConfig = {
  wave: Waveform;
  octave: number;
  semis: number;
  detuneCents: number;
  level: number;
  pwm?: number;
};

export type FilterType = "lowpass" | "highpass" | "bandpass";

export type FilterConfig = {
  type: FilterType;
  cutoffHz: number;
  resonance: number;
};

export type ModSource = "ampEnv" | "modEnv" | "lfo" | "key";
export type ModTarget = "pitch" | "cutoff" | "amp" | "pwm" | "fmDepth" | "amDepth";

export type ModRoute = {
  source: ModSource;
  target: ModTarget;
  depth: number;
  invert?: boolean;
};

/** Which carrier(s) osc3 modulates. */
export type FmTarget = "osc1" | "osc2" | "both";

/**
 * Osc3 → carrier modulation.
 *
 * `fmDepth` is a phase-modulation index in cycles: osc3's output is added to
 * the carrier's phase *read*, never to its accumulator, so unlike true FM the
 * pitch cannot drift when the modulator carries DC.
 *
 * `amDepth` blends dry carrier → ring modulation, so 0.5 is classic AM and
 * 1 is full ring mod. Both can run at once.
 */
export type FmConfig = {
  target: FmTarget;
  fmDepth: number;
  amDepth: number;
  /** How much note velocity scales fmDepth (0 = none, 1 = fully velocity-scaled). */
  velocityToFm: number;
};

export type NoiseConfig = {
  /** Mixed alongside the oscillators; 0 disables it. */
  level: number;
};

export type SynthPreset = {
  name: string;
  osc1: OscConfig;
  osc2: OscConfig;
  /** Third oscillator. Audible at level > 0, and doubles as the FM/AM modulator. */
  osc3?: OscConfig;
  /** Dedicated noise source, so an oscillator need not be spent on `wave: "noise"`. */
  noise?: NoiseConfig;
  fm?: FmConfig;
  filter: FilterConfig;
  ampEnv: Adsr;
  modEnv: Adsr;
  lfo: { rateHz: number; wave: "sine" | "tri"; depth: number };
  routes: ModRoute[];
  glideMs?: number;
};

/** Wavetable oscillator warp modes (v1). */
export type WtWarpMode = "none" | "bend" | "mirror" | "sync";

export type WtOscConfig = {
  tableId: string;
  /** Morph position across frames 0..1. */
  position: number;
  warp: number;
  warpMode: WtWarpMode;
  octave: number;
  semis: number;
  detuneCents: number;
  level: number;
};

export type WtModSource = "ampEnv" | "modEnv" | "lfo" | "key";
export type WtModTarget =
  | "pitch"
  | "cutoff"
  | "amp"
  | "tablePos1"
  | "tablePos2"
  | "warp1"
  | "warp2"
  | "unisonDetune"
  | "unisonSpread"
  | "unisonEdgeDecay"
  | "pmDepth"
  | "subLevel";

export type WtModRoute = {
  source: WtModSource;
  target: WtModTarget;
  depth: number;
  invert?: boolean;
};

export type WavetablePreset = {
  name: string;
  voices: number;
  unisonDetune: number;
  unisonSpread: number;
  unisonEdgeDecay: number;
  osc1: WtOscConfig;
  osc2: WtOscConfig;
  /** Osc2 hard-syncs to osc1. */
  sync: boolean;
  /** Osc1 → osc2 phase modulation depth (cycles). */
  pmDepth: number;
  sub: { wave: "sine" | "square"; octave: number; level: number };
  noise: NoiseConfig;
  filter: FilterConfig;
  ampEnv: Adsr;
  modEnv: Adsr;
  lfo: { rateHz: number; wave: "sine" | "tri"; depth: number };
  routes: WtModRoute[];
  glideMs?: number;
};

/** Decoded wavetable transferred into the kernel. */
export type WavetableRuntimeTable = {
  id: string;
  frameSize: 2048;
  frameCount: number;
  samples: Float32Array;
};

/** One pad in a procedural / sample drum kit (Impact-like one-shot). */
export type DrumPad = {
  name: string;
  /** 0..1 — silence when 0. */
  level: number;
  /** Body oscillator start pitch (Hz). */
  pitchHz: number;
  /** How far pitch falls over the hit (octaves). */
  pitchEnv: number;
  /** Amplitude decay time (ms) — synth-mode exp fade (also kept for legacy). */
  decayMs: number;
  /** Sine/tri body level 0..1. */
  toneLevel: number;
  /** Noise burst level 0..1. */
  noiseLevel: number;
  /** Short transient click 0..1. */
  clickLevel: number;
  /** Lowpass cutoff for the mix (Hz). */
  filterHz: number;
  /** Host library sample id — used when kit.mode === "sample". */
  sampleId?: string;
  /** Sample transpose in semitones (default 0). */
  pitchSemis?: number;
  /** Optional PCM region in frames for sample pads. */
  region?: { start: number; end: number };
  /**
   * Sample-mode amplitude ADSR (seconds).
   * Defaults to near-zero A/D/R and full sustain so oneshots play the WAV as-is
   * until EOF, `---`, or `Cxx` cut.
   */
  ampEnv?: Adsr;
};

/** Kit-level synth vs sample oneshot mode. */
export type DrumKitMode = "synth" | "sample";

/** Chromatic pads from `baseNote` (default C4 = 60); see DRUM_PAD_COUNT. */
export type DrumKit = {
  name: string;
  baseNote: number;
  /** Default "synth". When "sample", pads play sampleId oneshots. */
  mode?: DrumKitMode;
  pads: DrumPad[];
};

/** Lightweight SF2 patch reference — never embeds the font bytes. */
export type Sf2Patch = {
  fontId: string;
  bank: number;
  program: number;
  /** Linear gain 0..1. */
  gain: number;
};

/** Sampler playback engine mode. Granular DSP lands in a later phase. */
export type SamplerPlayMode = "classic" | "granular";

/** SF2-style sustain loop exit policy (when `loop.enabled`). */
export type SamplerLoopMode = "continuous" | "untilRelease";

export type SamplerLfoWave = "sine" | "tri" | "square" | "saw" | "snh" | "random";

export type SamplerModSource =
  | "ampEnv"
  | "modEnv"
  | "lfo"
  | "lfo2"
  | "key"
  | "velocity"
  | "random";

export type SamplerModTarget =
  | "pitch"
  | "cutoff"
  | "amp"
  | "pan"
  | "playbackPos"
  | "startOffset"
  | "loopStart"
  | "loopEnd"
  | "sampleRate"
  | "grainSize"
  | "grainDensity"
  | "grainSpray"
  | "stretch"
  | "filterRes";

export type SamplerModRoute = {
  source: SamplerModSource;
  target: SamplerModTarget;
  depth: number;
  invert?: boolean;
};

/** Keyboard / velocity map entry → one sample asset. */
export type SamplerZone = {
  id: string;
  sampleId: string;
  keyLow: number;
  keyHigh: number;
  rootKey: number;
  velLow: number;
  velHigh: number;
  region?: { start: number; end: number };
  tuneCents?: number;
  gainDb?: number;
  reverse?: boolean;
  padIndex?: number;
};

export type SampleSlice = {
  id: string;
  frame: number;
  label?: string;
  color?: number;
  /**
   * When true, explode skips the interval starting at this marker
   * (to the next marker). UI draws a left-pointing triangle.
   */
  omit?: boolean;
};

export type SamplerPreset = {
  name: string;
  playMode: SamplerPlayMode;
  /**
   * Classic mode only: pitch via granular overlap (stretch=1) so keyboard
   * notes keep the sample’s wall-clock length. Ignored when `playMode` is
   * granular (use Stretch there). Quality is grainy vs true phase-vocoder.
   */
  preserveDuration?: boolean;
  /** Default region when a zone omits `region` (frames). */
  region: { start: number; end: number };
  loop: {
    enabled: boolean;
    start: number;
    end: number;
    xfade?: number;
    /**
     * SF2-style loop exit:
     * - `continuous` — keep looping through amp release (oscillator / one-cycle).
     * - `untilRelease` — leave the loop on note-off and play the post-loop tail.
     */
    mode?: SamplerLoopMode;
  };
  reverse: boolean;
  granular: {
    grainSizeMs: number;
    density: number;
    spray: number;
    window: "hann" | "tri" | "rect";
    stretch: number;
  };
  filter: FilterConfig;
  ampEnv: Adsr;
  modEnv: Adsr;
  lfo: { rateHz: number; wave: SamplerLfoWave; depth: number };
  lfo2?: { rateHz: number; wave: SamplerLfoWave; depth: number };
  routes: SamplerModRoute[];
  glideMs?: number;
  maxVoices?: number;
  zones: SamplerZone[];
  slices?: SampleSlice[];
  primarySampleId?: string;
};

/**
 * Host sample-library folder (arbitrary nesting via parentId).
 * Folders organize the Samples panel only — instruments still reference sample ids.
 */
export type SampleFolderMeta = {
  id: string;
  name: string;
  /** Null / omitted = top-level under Library. */
  parentId?: string | null;
  /** Sibling order within the same parent (lower first). */
  sortKey: number;
  createdAt: string;
};

/**
 * Host-side sample catalog entry — PCM lives in OPFS / package / kernel transfer.
 * Never embed bytes inside instrument JSON.
 */
export type SampleAssetMeta = {
  id: string;
  name: string;
  sampleRate: number;
  channels: 1 | 2;
  lengthFrames: number;
  rootKey?: number;
  source?: "import" | "record" | "factory" | "chop" | "bounce";
  /** Factory one-cycles use cycle pitch formula instead of root-key ratio. */
  oneCycle?: boolean;
  createdAt: string;
  /** Samples panel folder; null / omitted = Library root. */
  folderId?: string | null;
  /** Sibling order within the same folder (lower first). */
  sortKey?: number;
};

/** Decoded mono PCM ready for the kernel sample map. */
export type SamplerRuntimeSample = {
  id: string;
  name: string;
  sampleRate: number;
  pcm: Float32Array;
  oneCycle?: boolean;
  rootKey?: number;
};

export type Sf2PresetMeta = {
  bank: number;
  program: number;
  name: string;
};

/** One playable sample zone inside a loaded runtime bank. */
export type Sf2Zone = {
  bank: number;
  program: number;
  sampleIndex: number;
  keyLo: number;
  keyHi: number;
  velLo: number;
  velHi: number;
  rootKey: number;
  /** Coarse+fine tune in cents. */
  tuneCents: number;
  /** Sample rate of the source PCM. */
  sampleRate: number;
  /** Inclusive start index in the sample buffer. */
  sampleStart: number;
  /** Exclusive end index in the sample buffer. */
  sampleEnd: number;
  loopStart: number;
  loopEnd: number;
  /** 0 = no loop, 1 = continuous, 3 = loop until noteOff then release. */
  loopMode: number;
  /** Linear amplitude from initial attenuation. */
  gain: number;
};

/** Decoded bank transferred into the kernel (PCM + zones). */
export type Sf2RuntimeBank = {
  fontId: string;
  presets: Sf2PresetMeta[];
  samples: Float32Array[];
  zones: Sf2Zone[];
};

/** Per-slot instrument (synth voice, drum kit, soundfont, wavetable, sampler, or external MIDI). */
export type Instrument =
  | { kind: "synth"; preset: SynthPreset }
  | { kind: "drums"; kit: DrumKit }
  | { kind: "sf2"; patch: Sf2Patch }
  | { kind: "wavetable"; preset: WavetablePreset }
  | { kind: "sampler"; preset: SamplerPreset }
  | { kind: "externalMidi"; patch: ExternalMidiPatch };

/**
 * Tracker track that sends noteon/noteoff (+ export-parity CCs) to a Web MIDI output.
 * Silent in the Adam audio kernel.
 */
export type ExternalMidiPatch = {
  /** Web MIDI `MIDIOutput.id`; empty = no port selected. */
  outputDeviceId: string;
  /** MIDI channel 1–16. */
  channel: number;
};

/** Per-slot channel strip mix (dock + kernel). */
export type SlotMix = {
  /** Linear gain 0..1 (default 1). */
  volume: number;
  /** −1 = full left, 0 = center, +1 = full right. */
  pan: number;
};

export type FxType =
  | "distortion"
  | "bitcrush"
  | "delay"
  | "chorus"
  | "reverb"
  | "colorReverb"
  | "compressor"
  | "eq"
  | "utility"
  | "filter"
  | "limiter"
  | "flanger"
  | "phaser"
  | "saturation";

export type FxNode = {
  id?: string;
  type: FxType;
  enabled: boolean;
  wet: number;
  params: Record<string, number>;
};

/** Instrument slot index 0..MAX_INSTRUMENT_SLOT_COUNT-1 (API may expose 1-based). */
export type InstrumentSlot = number;

/** Default track count for new projects / empty clips. */
export const DEFAULT_INSTRUMENT_SLOT_COUNT = 8;

/** Hard capacity for kernel voices and project tracks. */
export const MAX_INSTRUMENT_SLOT_COUNT = 128;

/**
 * @deprecated Prefer DEFAULT_INSTRUMENT_SLOT_COUNT or a live project track count.
 * Kept as an alias of the default so older call sites keep compiling during migration.
 */
export const INSTRUMENT_SLOT_COUNT = DEFAULT_INSTRUMENT_SLOT_COUNT;

export function clampInstrumentSlotCount(n: number): number {
  if (!Number.isFinite(n)) {
    return DEFAULT_INSTRUMENT_SLOT_COUNT;
  }
  return Math.min(MAX_INSTRUMENT_SLOT_COUNT, Math.max(1, Math.round(n)));
}

export function allInstrumentSlots(
  count: number = DEFAULT_INSTRUMENT_SLOT_COUNT,
): InstrumentSlot[] {
  const n = clampInstrumentSlotCount(count);
  return Array.from({ length: n }, (_, i) => i);
}

/**
 * Decoded tracker note-FX (host → kernel). Letter codes stay on the host;
 * the kernel only sees these numeric controls.
 */
export type VoiceNoteFxParams = {
  vibratoSpeedHz?: number;
  vibratoDepthSemi?: number;
  /** Semitones per second (signed). */
  slideSemiPerSec?: number;
  /** Semitones per second toward target note. */
  portaSemiPerSec?: number;
  /** Added to filter cutoff in octaves. */
  cutoffOct?: number;
  /**
   * Force amp/mod envelopes (and osc phases) to restart from zero even if the
   * voice is still active. Used by tracker retrigger / ratchet so in-step hits
   * punch instead of soft-legato “squishing”.
   */
  hardRetrigger?: boolean;
  /** Arpeggio first interval (semitones); 0 = skip that stage. */
  arpSemi1?: number;
  /** Arpeggio second interval (semitones); 0 = skip that stage. */
  arpSemi2?: number;
  /** Arpeggio stage rate (stages per second). 0 disables. */
  arpHz?: number;
  /**
   * Voice pan −1..1 for this note event (`Bxx`).
   * Omit to use the slot / pad default pan from `SlotMix`.
   */
  pan?: number;
};

/** Host → kernel messages (worklet port or direct handleMessage). */
export type KernelMessage =
  | { type: "init"; sampleRate: number }
  | { type: "setBpm"; bpm: number }
  | { type: "transport"; playing: boolean; /** AudioContext time when play started, or 0 */ startTime: number }
  | { type: "setInstrument"; slot: InstrumentSlot; instrument: Instrument }
  | { type: "loadSf2Bank"; fontId: string; bank: Sf2RuntimeBank }
  | { type: "loadWavetable"; table: WavetableRuntimeTable }
  | { type: "loadSample"; sample: SamplerRuntimeSample }
  | { type: "setSlotMix"; slot: InstrumentSlot; mix: SlotMix }
  | { type: "setSlotMixes"; mixes: SlotMix[] }
  | { type: "setFxStack"; effects: FxNode[] }
  | { type: "setFx"; index: number; patch: Partial<FxNode> }
  /** Soft-bypass master FX (ramps wet; keeps delay/reverb memory warm). */
  | { type: "setFxBypass"; bypass: boolean }
  | { type: "clearFx" }
  | { type: "setSlotFxStack"; slot: InstrumentSlot; effects: FxNode[] }
  | { type: "setSlotFxStacks"; stacks: FxNode[][] }
  | { type: "clearSlotFx"; slot?: InstrumentSlot }
  | {
      type: "noteOn";
      slot: InstrumentSlot;
      /** MIDI note number */
      note: number;
      velocity: number;
      /** AudioContext time; 0 = ASAP */
      time: number;
      /** Optional decoded tracker note FX for this hit. */
      noteFx?: VoiceNoteFxParams;
    }
  | {
      type: "noteOff";
      slot: InstrumentSlot;
      note?: number;
      time: number;
    }
  | {
      type: "setNoteFx";
      slot: InstrumentSlot;
      noteFx: VoiceNoteFxParams;
      /** Optional velocity (Vol column on FX-only rows). */
      velocity?: number;
      /** AudioContext time; 0 = ASAP */
      time: number;
    }
  | { type: "allNotesOff" }
  /**
   * Drop timed noteOn / noteOff / setNoteFx with `time > afterTime`.
   * Keeps currently sounding voices; used when live pattern edits need an
   * immediate lookahead rebuild.
   */
  | { type: "cancelPending"; afterTime: number };

export type KernelInitOptions = {
  sampleRate: number;
};
